import React, {PureComponent, Fragment} from "react"
import {Route as RRRoute} from "react-router-dom"
import {omit} from "lodash"
import {Grid} from "react-bootstrap"
import styled from "styled-components"
import Helmet from "react-helmet"

import Loading from "@irati/ui/src/Loading"
import Loader from "@irati/ui/src/Loading/Loader"
import {injectIntl, getMessage, InjectIntl} from "../../lang"
import {RouteHasPermissionTo} from "../../permissions"
import {AuthedCheckerContext, SetAuthStatus} from "./AuthedChecker"

const StyledLoader = styled.div.attrs({
  children: <Loader />
})`
  .loader {
    margin-top: 100px;
  }
`

const Div = (props) => <div {...props} />

export interface NestedRouteProps extends InjectIntl {
  permissions?: string | string[]
  exact?: boolean
  match: string
  path?: string
  title?: string
  values?: any
  component?: any
  componentProps?: {[key: string]: any}
  unauthed?: boolean
  noauth?: boolean
  useGrid?: boolean
}

/**
 * Core routed-page wrapper.
 *
 * Responsibilities:
 * 1. Builds the *full* route path by concatenating the parent `match` value
 *    with its own `path` prop so that nested routing works seamlessly.
 * 2. Runs permission & authentication checks (`RouteHasPermissionTo`,
 *    `AuthedCheckerContext`).
 * 3. Shows a loading spinner until auth status is known.
 * 4. Sets the document `<title>` via `react-helmet` using i18n aware helpers.
 * 5. Wraps the page component (`component`) in a Bootstrap `Grid` unless
 *    `useGrid` is set to `false`.
 *
 * All higher-order helpers (currently `withPartialRoute`) ultimately
 * render this component (or its `PartialRoute` variant), so understanding this
 * file explains the whole routed-page pipeline.
 */
class NestedRoute extends PureComponent<NestedRouteProps> {
  static defaultProps = {
    title: "",
    component: null,
    componentProps: null,
    unauthed: false,
    noauth: false,
    useGrid: true
  }

  render() {
    const {formatMessage, title, values, useGrid, component: App, componentProps, unauthed, noauth, permissions} = this.props

    const appProps = {...omit(this.props, "intl"), ...componentProps}

    const Wrapper = useGrid ? Grid : Div

    return (
      <AuthedCheckerContext.Consumer>
        {({load, setAuthed}) => (
          <Fragment>
            <SetAuthStatus setAuthed={setAuthed} unauthed={unauthed} noauth={noauth} />
            <RouteHasPermissionTo permissions={permissions}>
              <Loading load={load} Loader={() => <StyledLoader />}>
                <Helmet titleTemplate="Perkbox | %s">
                  <title>{title ? getMessage(formatMessage, title, values) : ""}</title>
                </Helmet>
                <Wrapper>
                  <App {...appProps} />
                </Wrapper>
              </Loading>
            </RouteHasPermissionTo>
          </Fragment>
        )}
      </AuthedCheckerContext.Consumer>
    )
  }
}

const Route: React.FC<NestedRouteProps> = (props) => {
  const {match, path, component, ...rest} = props
  const nestedProps = {component, ...rest}
  return <RRRoute {...rest} path={`${match}${path}`} render={(p) => <NestedRoute {...nestedProps} {...p} />} />
}

Route.defaultProps = {
  match: "",
  path: "",
  useGrid: true
}

export default injectIntl(Route)
